import React, { useMemo, useState } from 'react';
import { getMultiSelectionState } from '@components/MainStage/multiSelection.store';
import useStore from '@components/MainStage/store';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import useAssets from '@hooks/useAssets';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { useRemoveNodesFromAssetMutation } from '@store/services/nodes';
import {
  INodeActionOutcome,
  NodeActionResult,
  NodeDTO,
} from '@store/services/nodes/types';
import { parseErrorResponse } from '@utils/helpers';
import {
  isRemoveFromAssetVisible,
  REMOVE_FROM_ASSET_ACTION as config,
} from '@utils/nodeActions';

import { NodeActionConfirmModal } from './NodeActionConfirmModal';

export const useRemoveNodesFromAsset = (
  targetIds: string[],
  onCloseMenu: () => void,
) => {
  const { getNodeById, nodes } = useStore();
  const {
    toolbox: { mode },
  } = useProject();
  const { selectedAssetId } = useAssets();
  const { showToast } = useToast();
  const { saveResponseToRedux } = useSaveEntitiesResponse();
  const [removeNodesFromAsset, { isLoading }] =
    useRemoveNodesFromAssetMutation();
  const [pendingOutcomes, setPendingOutcomes] = useState<
    INodeActionOutcome[] | null
  >(null);

  const targetIdsKey = targetIds.join(',');

  const targetNodes = useMemo(
    () =>
      targetIds
        .map((id) => getNodeById(id)?.data?.dto as NodeDTO | undefined)
        .filter((dto): dto is NodeDTO => Boolean(dto)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetIdsKey, nodes, getNodeById],
  );

  const isVisible = mode
    ? isRemoveFromAssetVisible(targetNodes, mode, selectedAssetId)
    : false;

  const nameOf = (id: string) =>
    (getNodeById(id)?.data?.dto as NodeDTO | undefined)?.name ?? id;

  const run = async (force: boolean) => {
    if (!selectedAssetId) return;

    try {
      const response = await removeNodesFromAsset({
        assetId: selectedAssetId,
        nodeIds: targetIds,
        force,
      }).unwrap();

      const updated = response.outcomes.filter(
        (o) => o.result === NodeActionResult.Updated,
      );

      if (!response.applied) {
        if (!updated.length) {
          const hasBlocked = response.outcomes.some(
            (o) => o.result === NodeActionResult.Blocked,
          );
          showToast(
            hasBlocked ? config.allBlockedMessage : config.noneRemovableMessage,
            'error',
          );
          setPendingOutcomes(null);
          onCloseMenu();
          return;
        }
        setPendingOutcomes(response.outcomes);
        return;
      }

      saveResponseToRedux(response);
      getMultiSelectionState().removeMany(updated.map((o) => o.node_id));
      showToast(
        config.successMessageWithCount?.(updated.length) ??
          config.successMessage,
        'success',
      );
      setPendingOutcomes(null);
      onCloseMenu();
    } catch (error) {
      showToast(parseErrorResponse(error), 'error');
      setPendingOutcomes(null);
      onCloseMenu();
    }
  };

  const start = () => {
    if (isLoading) return;
    return run(false);
  };

  const outcomes = pendingOutcomes ?? [];
  const toItem = (outcome: INodeActionOutcome) => ({
    id: outcome.node_id,
    name: nameOf(outcome.node_id),
  });

  const toChange = outcomes
    .filter((o) => o.result === NodeActionResult.Updated)
    .map(toItem);
  const skipped = outcomes
    .filter(
      (o) =>
        o.result === NodeActionResult.Ineligible ||
        o.result === NodeActionResult.Blocked,
    )
    .map(toItem);

  const allEligible = !skipped.length;

  const modal = (
    <NodeActionConfirmModal
      open={pendingOutcomes !== null}
      title={config.menuLabel}
      text={
        allEligible
          ? config.menuTextAllEligible(toChange.map(({ name }) => name))
          : config.menuText
      }
      toChange={toChange}
      toChangeTitle={config.menuToChangeTitle}
      skipped={skipped}
      toSkipTitle={config.menuToSkipTitle}
      confirmName={config.menuConfirmName}
      backName={allEligible ? config.menuBackNameAllEligible : undefined}
      showItemLists={!allEligible}
      contentSx={{ gap: 3 }}
      onBack={() => setPendingOutcomes(null)}
      onConfirm={() => {
        if (isLoading) return;
        return run(true);
      }}
    />
  );

  return { isVisible, isLoading, start, modal };
};
